import * as paper from 'paper';
import {Point, Group, Path, Size, Raster, PointText} from "paper";
import {MathFunctions, SimpleSize} from "./includes/mathHelper";
import {DrawDataLoader} from "./drawDataLoader";
import {ApiService} from "./includes/apiService";
import {EEToolAdapterContextType} from "../types/EEToolAdapterContextType";

export class DrawDisplayData {

    canvas: HTMLCanvasElement;
    adapter: EEToolAdapterContextType;

    static DEFAULT_MESASUREMENT_TEXT_STYLE = {
        fontSize: 16,
        justification: 'center'
    }

    static ANGLE_DISPLAY_STYLE = {
        fontSize: 17,
        justification: 'center',
        strokeWidth: 0.2,
        strokeColor: new paper.Color('blue')
    }

    static CUTOUT_IMAGESIZE = new Size(12, 20);
    static ECLEGEND_SPACER = new Size(40, 10);
    static ECLEGEND_ICON_SIZE = new Size(100, 50);

    static BACKSPLASH_LEGEND_ICON_SIZE = DrawDisplayData.ECLEGEND_ICON_SIZE;
    static ECLEGEND_POSITION = {top: false, left: true};
    static ECLEGEND_LABEL_SHIFT = 3;
    static ECLEGEND_LABEL_STYLE = {
        fillColor: new paper.Color('black'),
        fontFamily: 'Courier New',
        fontWeight: 'bold',
        fontSize: 16,
        justification: 'center',
        paddingLeft: 20
    };

    static NULL_SINK_SIZE = new Size(128, 86);

    static STROKESTYLE = {
        strokeColor: new paper.Color('green'),
        strokeWidth: 0.7,
        fillColor: new paper.Color('white')
    };

    static INVALID_EDGE_STROKESTYLE = {
        strokeColor: new paper.Color('red'),
        strokeWidth: 3,
        fillColor: new paper.Color('red')
    };

    static RADIUS_LABEL_SHIFT = 30;
    static CORNER_RADIUS_LABEL_SHIFT = 30;
    static RADIUS_LEGEND_STYLE = {
        fillColor: new paper.Color('red'),
        strokeColor: new paper.Color('black'),
        strokeWidth: 0.7,
        fontFamily: 'Courier New',
        fontWeight: 'bold',
        fontSize: 12
    }
    static RADIUS_LABEL_STYLE = {
        fillColor: new paper.Color('red'),
        strokeColor: new paper.Color('red'),
        strokeWidth: 0.7,
        fontSize: 14,
        fontWeight: 'bold',
        justification: 'center'
    }

    //Shift Edge from CounterTop
    static EDGE_BAR_SHIFT = -2;
    static EDGE_STYLE = {
        fillColor: new paper.Color('#969696'),
        strokeColor: new paper.Color('black')
    };

    static EDGE_LAMINATE_CAP_STYLE = {
        fillColor: new paper.Color('#FFCCCC'),
        strokeColor: new paper.Color('black')
    };

    static EDGE_LAMINATE_SPLASH_STYLE = {
        fillColor: new paper.Color('#CCCCFF'),
        strokeColor: new paper.Color('black')
    };

    static EDGE_LAMINATE_CAP_SPLASH_STYLE = {
        fillColor: new paper.Color('#FFCCFF'),
        strokeColor: new paper.Color('black')
    };

    static EDGE_PIXEL_THICKNESS = 5;

    static INVISIBLE_HANDLE_STYLE = {
        fillColor: new paper.Color('lightblue'),
        strokeColor: new paper.Color('lightblue')
    };
    static INVISIBLE_HANDLE_REDUCE_WIDTH = 16;
    static INVISIBLE_HANDLE_REDUCE_HEIGHT = 8;
    static INVISIBLE_HANDLE_OPACITY = 0;

    static SPLASH_LABEL_SHIFT = 18;
    static SPLASH_BAR_SHIFT = 0;
    static SPLASH_STYLE = {
        strokeColor: new paper.Color('black'),
        fillColor: new paper.Color('yellow')
    };

    static SPLASH_LABEL_STYLE = {
        strokeColor: new paper.Color('black'),
        fillColor: new paper.Color('dimgray'),
        strokeWidth: 0.7,
        fontSize: 14,
        justification: 'center'
    }

    //Css for CookTop Splash
    static COOKTOP_SPLASH_LABEL_SHIFT = 18;
    static COOKTOP_SPLASH_BAR_SHIFT = 0;
    static COOKTOP_SPLASH_STYLE = {
        strokeColor: new paper.Color('black'),
        fillColor: new paper.Color('blue')
    };
    static COOKTOP_SPLASH_LABEL_STYLE = {
        strokeColor: new paper.Color('dimgray'),
        fillColor: new paper.Color('dimgray'),
        strokeWidth: 0.7,
        fontSize: 14,
        justification: 'center'
    }

    static MEASUREMENT_LABEL_STYLE = {
        strokeColor: new paper.Color('black'),
        strokeWidth: 0.7,
        fontSize: 15,
        justification: 'center'
    };

    static MEASUREMENT_ARROWSIZE = 5;

    static CANVASWIDTH: number = 1200;
    static CANVASHEIGHT: number = 800;

    static DEFAULT_EDGE_BUFFER = 100;
    static TOP_BUFFER = DrawDisplayData.DEFAULT_EDGE_BUFFER;
    static LEFT_BUFFER = DrawDisplayData.DEFAULT_EDGE_BUFFER;
    static BOTTOM_BUFFER = DrawDisplayData.CANVASHEIGHT - DrawDisplayData.DEFAULT_EDGE_BUFFER;
    static RIGHT_BUFFER = DrawDisplayData.CANVASWIDTH - DrawDisplayData.DEFAULT_EDGE_BUFFER;

    static DRAW_COUNTERS_VISIBILITY_TEMPLATE =
        {
            pathCursor: 'move',
            curveCursor: 'drag',
            segmentCursor: 'drag',
            showBasicMeasurements: true,
            allowAlteringMeasurements: true,
            allowAlteringAngles: true,
            pushMaterialColorLabelBack: true,
            showEdges: true,
            //showEdgeLabels: true,
            showEdgeLegends: true,
            showRadiusMeasures: true,
            showBumpHandles: true,
            showBacksplashPath: true,
            showBacksplashMeasures: true,
            showSinkItems: true,
            alwaysSuppressCursors: false

        };
    static EDGE_COLOR_VISIBILITY_TEMPLATE =
        {
            pathCursor: 'click',
            curveCursor: 'menu',
            segmentCursor: 'menu',
            showBasicMeasurements: true,
            allowAlteringMeasurements: false,
            showEdges: true,
            //showEdgeLabels: true,
            showEdgeLegends: true,
            showRadiusMeasures: true,
            showBumpHandles: true,
            showBacksplashPath: true,
            showBacksplashMeasures: true,
            showSinkItems: true,
            alwaysSuppressCursors: false

        };
    static BUMPS_RADIUSES_VISIBILITY_TEMPLATE =
        {
            curveCursor: 'menu',
            segmentCursor: 'menu',
            showBasicMeasurements: true,
            allowAlteringMeasurements: true,
            pushMaterialColorLabelBack: true,
            showEdges: true,
            //showEdgeLabels: true,
            showEdgeLegends: true,
            showRadiusMeasures: true,
            showBumpHandles: true,
            showBacksplashPath: true,
            showBacksplashMeasures: true,
            showSinkItems: true,
            alwaysSuppressCursors: false

        };
    static BACKSPLASH_VISIBILITY_TEMPLATE =
        {
            //pathCursor: 'move',
            pathCookTopSplashCursor: 'move',
            curveCursor: 'menu',
            cutoutCursor: 'move',
            cookTopSplashCursor: 'move',
            showBasicMeasurements: true,
            allowAlteringMeasurements: false,
            pushMaterialColorLabelBack: true,
            showEdges: true,
            //showEdgeLabels: true,
            showEdgeLegends: true,
            showRadiusMeasures: true,
            showBumpHandles: true,
            showBacksplashPath: true,
            showBacksplashMeasures: true,
            showSinkItems: true,
            alwaysSuppressCursors: false

        };
    static SINK_COOKTOP_VISIBILITY_TEMPLATE =
        {
            pathCursor: 'menu',
            sinkCursor: 'move',
            showBasicMeasurements: true,
            allowAlteringMeasurements: false,
            showEdges: true,
            //showEdgeLabels: true,
            showEdgeLegends: true,
            showRadiusMeasures: true,
            showBumpHandles: true,
            showBacksplashPath: true,
            showBacksplashMeasures: true,
            showSinkItems: true,
            alwaysSuppressCursors: false

        };
    static PRICING_PRINTING_VISIBILITY_TEMPLATE =
        {
            showBasicMeasurements: true,
            allowAlteringMeasurements: false,
            showEdges: true,
            //showEdgeLabels: true,
            showEdgeLegends: true,
            showRadiusMeasures: true,
            showBumpHandles: true,
            showBacksplashPath: true,
            showBacksplashMeasures: true,
            showSinkItems: true,
            alwaysSuppressCursors: true

        };

    static MATERIAL_LABEL_STYLE = {
        fontSize: 16,
        justification: 'center',
        fontWeight: 'bold'
    }

    static QUOTE_INFORMATION_VISIBILITY_TEMPLATE =
        {
            showBasicMeasurements: false,
            allowAlteringMeasurements: false,
            showEdges: false,
            //showEdgeLabels: false,
            showEdgeLegends: false,
            showRadiusMeasures: true,
            showBumpHandles: false,
            showBacksplashPath: false,
            showBacksplashMeasures: false,
            showSinkItems: false,
            alwaysSuppressCursors: true,
            allowAlteringAngles: false,
            suppressCursors: false,
            suppressAltCursors: false,
            segmentCursor: 'default',
            curveCursor: 'default',
            pathCursor: 'default',
            sinkCursor: 'default',
            cookTopSplashCursor: 'default',
            cutoutCursor: 'default'
        };

    static DEFAULT_LINE_RADIUS_DEPTH_INCHES = 10;
    static DEFAULT_CORNER_RADIUS_DEPTH_INCHES = 0.75;
    static DEFAULT_CORNER_DIAGONAL_DEPTH_INCHES = 5;//was 15

    static DEFAULT_OTHER_COOKTOP_SPLASH_WIDTH = 40;
    static DEFAULT_OTHER_COOKTOP_SPLASH_HEIGHT = 15;

    currentECLegendArrGroup: any;

    constructor(canvas: HTMLCanvasElement, adapter: EEToolAdapterContextType) {
        this.canvas = canvas;
        this.adapter = adapter;
    }

    makeSinkGroup(sinkTypeData: any, currentPosition: any, currentAngle: any, currentSize: any, addFunc: any) {
        var retVal;
        var typeEnum = sinkTypeData.typeEnum;
        switch (typeEnum) {
            case 1: {
                if (sinkTypeData.ironType >= 0) {
                    retVal = DrawDisplayData.AddFreeSink(currentPosition, currentAngle, currentSize, sinkTypeData.sinkID, addFunc);
                } else {
                    retVal = this.AddSink(sinkTypeData.sinkID, currentPosition, currentAngle, addFunc);
                }
                break;
            }
            case 2: {
                if (sinkTypeData.sinkIDFunc) {
                    sinkTypeData.sinkID = sinkTypeData.sinkIDFunc();
                    sinkTypeData.sinkIDFunc = null;
                }
                retVal = this.addCutout(sinkTypeData.sinkID, currentPosition, currentAngle, currentSize, addFunc);
                break;
            }
            case 3: {
                if (sinkTypeData.sinkID == -1)
                    retVal = DrawDisplayData.AddCookTopCutout(currentPosition, currentAngle, currentSize, addFunc);
                else {
                    retVal = DrawDisplayData.AddFreeSink(currentPosition, currentAngle, currentSize, sinkTypeData.sinkID, addFunc);
                }
                break;
            }
            case 4: //typeEnum = 4: Refrigerator
            {
                retVal = DrawDisplayData.AddCookTopCutoutOption(1, currentPosition, currentAngle, currentSize, addFunc);
                break;
            }
            case 5: //typeEnum = 5: Range
            {
                retVal = DrawDisplayData.AddCookTopCutoutOption(2, currentPosition, currentAngle, currentSize, addFunc);
                break;
            }
            case 6: //typeEnum = 6: Dish Washer
            {
                retVal = DrawDisplayData.AddCookTopCutoutOption(3, currentPosition, currentAngle, currentSize, addFunc);
                break;
            }
        }

        return retVal;
    }

    static AddCookTopCutoutOption(typeId: any, currentPoint: any, currentAngle: any, currentSize: any, addFunc: any) {
        var sink;
        switch (typeId) {
            case 1: {
                sink = new Raster(DrawDataLoader.COOKTOP_REFRIGERATOR_IMAGE, currentPoint);
                break;
            }
            case 2: {
                sink = new Raster(DrawDataLoader.COOKTOP_RANGE_IMAGE, currentPoint);
                break;
            }
            case 3: {
                sink = new Raster(DrawDataLoader.COOKTOP_DISHWASHER_IMAGE, currentPoint);
                break;
            }
            default: {
                sink = new Raster(DrawDataLoader.COOKTOP_CUTOUT_IMAGE, currentPoint);
                break;
            }
        }

        var anchor = DrawDisplayData.makeRasterBase(currentPoint, currentSize, currentAngle, sink);
        DrawDisplayData.positionLoadedRaster(sink, currentSize, currentPoint, currentAngle);
        sink = new Group([anchor, sink]);
        if (addFunc)
            addFunc(sink);
        return sink;

    }

    AddSink(sinkID: any, currentPoint: any, currentAngle: any, addFunc: any) {
        //var path = 'Pages/Store/QuotingTool/Images/Line Drawings/Sinks/';

        // HPRO-132 : EE: Got Stack Trace Error when add Non-Cast Iron Sink
        if (sinkID !== parseInt(sinkID, 10)) return; // check sinkID is int
        // End HPRO-132

        var size;
        var domImage = ApiService.getImageFromProductID(sinkID, this.adapter);
        var sink = new Raster(domImage, currentPoint);
        var anchor = DrawDisplayData.makeRasterBase(currentPoint, null, currentAngle, sink);
        DrawDisplayData.positionLoadedRaster(sink, size, currentPoint, currentAngle);
        var group = new Group([anchor, sink]);
        if (addFunc)
            addFunc(group);
        return group;
    }

    addCutout(sinkID: any, currentPoint: any, currentAngle: any, currentSize: any, addFunc: any) {
        var domImage = ApiService.getImageFromProductID(sinkID, this.adapter);
        var sink = new Raster(domImage, currentPoint);
        //var sink = new Raster("Pages/Store/QuotingTool/Images/3x5ElectricalCutout.png", currentPoint);
        var anchor = DrawDisplayData.makeRasterBase(currentPoint, currentSize, currentAngle, sink);
        DrawDisplayData.positionLoadedRaster(sink, (currentSize) ? currentSize : DrawDisplayData.CUTOUT_IMAGESIZE, currentPoint, currentAngle);
        var group = new Group([anchor, sink]);
        if (addFunc)
            addFunc(group);
        return group;
    }

    static AddCookTopCutout(currentPoint: any, currentAngle: any, currentSize: any, addFunc: any) {
        var sink = new Raster(DrawDataLoader.COOKTOP_CUTOUT_IMAGE, currentPoint);
        var anchor = DrawDisplayData.makeRasterBase(currentPoint, currentSize, currentAngle, sink);
        DrawDisplayData.positionLoadedRaster(sink, currentSize, currentPoint, currentAngle);
        var group = new Group([anchor, sink]);
        if (addFunc)
            addFunc(group);
        return group;

    }

    static AddFreeSink(currentPoint: any, currentAngle: any, currentSize: any, freeSinkTypeEnum: any, addFunc: any) {
        var sinkFile;
        switch (freeSinkTypeEnum) {
            case 0:
            case 10: {
                sinkFile = DrawDataLoader.SINGLE_SINK_IMAGE;
                break;
            }
            case 1:
            case 11: {
                sinkFile = DrawDataLoader.SINK_IMAGE_50_50;
                break;
            }
            case 2:
            case 12: {
                sinkFile = DrawDataLoader.SINK_IMAGE_60_40;
                break;
            }
            case 3:
            case 13: {
                sinkFile = DrawDataLoader.SINK_IMAGE_70_30;
                break;
            }
            case 4:
            case 14: {
                sinkFile = DrawDataLoader.SINK_IMAGE_40_60;
                break;
            }
            case 5:
            case 15: {
                sinkFile = DrawDataLoader.SINK_IMAGE_30_70;
                break;
            }
            default: {
                sinkFile = DrawDataLoader.SINGLE_SINK_IMAGE;
            }
        }

        var sink = new Raster(sinkFile, currentPoint);
        var anchor = DrawDisplayData.makeRasterBase(currentPoint, currentSize, currentAngle, sink);
        DrawDisplayData.positionLoadedRaster(sink, currentSize, currentPoint, currentAngle);
        var group = new Group([anchor, sink]);
        if (addFunc)
            addFunc(group);
        return group;
    }

    addECLegendArrGroup(ecLegendArr: any) {
        this.removeECLegendArrGroup();
        if (ecLegendArr && ecLegendArr.length > 0) {
            this.currentECLegendArrGroup = this.createECLegendArrGroup(ecLegendArr);
        }
    }

    createECLegendArrGroup(ecLegendArr: any) {
        var retVal;
        var groupArr = [], entry, hashtable: any = {};
        for (var i = 0; i < ecLegendArr.length; i++) {
            entry = this.createVisualECLegendEntry(ecLegendArr[i].e1, ecLegendArr[i].e1Text, ecLegendArr[i].b1, ecLegendArr[i].b1Text);
            if (entry) {
                var skipElement = false;
                if (entry instanceof Group) {
                    for (var t = 0; t < entry.children.length; t++) {
                        if (entry.children[t] instanceof paper.PointText) {
                            var content = (<paper.PointText>entry.children[t]).content;
                            if (hashtable[content]) {
                                skipElement = true;
                            } else {
                                hashtable[content] = true;
                            }
                            break;
                        }
                    }
                }

                if (skipElement) {
                    entry.remove();
                    continue;
                }

                for (var j = 0; j < groupArr.length; j++) {
                    var widthOffset = entry.bounds.width + DrawDisplayData.ECLEGEND_SPACER.width;
                    groupArr[j].position.x += (DrawDisplayData.ECLEGEND_POSITION.left) ? widthOffset : -1 * widthOffset;
                }
                groupArr.push(entry);
            }
        }
        if (groupArr.length > 0) {
            retVal = new Group(groupArr);
            if (retVal.bounds.x < 0) {
                retVal.position.x -= retVal.bounds.x;
            }
            retVal.position = MathFunctions.addVectors(retVal.position, DrawDisplayData.getECLegendArrLocation());
            retVal.bringToFront();
        }
        return retVal;
    }

    createVisualECLegendEntry(e1: any, e1Text: any, b1: any, b1Text: any) {//, e2, e2Text) {
        var e = this.createSingleVisualEC(e1, e1Text, false);
        //var e2 = createSingleVisualEC(e2, e2Text, true);
        var b = this.createSingleVisualEC(b1, b1Text, true);

        return (e && b) ? new Group([e, b]) : (e) ? e : b;
    }

    createSingleVisualEC(eID: any, eText: any, isBacksplash: any) {
        if (!eID || isNaN(eID)) {
            return;
        }
        var ecLegend;
        if (isBacksplash) {
            ecLegend = this.getBacksplashRasterByID(Number(eID));
        } else {
            ecLegend = this.getECRasterByID(Number(eID));//new Raster("Pages/Store/QuotingTool/Images/Sink 70-30.png", currentPoint); //set topLeft at 0,0
        }
        if (!ecLegend) {
            return;
        }

        ecLegend = DrawDisplayData.addBoundingLineAndText(ecLegend, eText);

        if (isBacksplash) {
            ecLegend.position.y += DrawDisplayData.ECLEGEND_POSITION.top ? -1 * DrawDisplayData.getECLegendArrYShift() : DrawDisplayData.getECLegendArrYShift();
        }

        return ecLegend;
    }

    //Set bounding line and text for EDGE
    static addBoundingLineAndText(ecLegend: any, eText: any) {
        var retGroup = new Group([ecLegend]);

        var ptLoc = new Point(ecLegend.position);
        ptLoc.y += DrawDisplayData.ECLEGEND_POSITION.top ? .5 * DrawDisplayData.getECLegendArrYShift() : -1 * .5 * DrawDisplayData.getECLegendArrYShift();
        ptLoc.y -= DrawDisplayData.ECLEGEND_LABEL_SHIFT;

        ptLoc.x += DrawDisplayData.ECLEGEND_LABEL_STYLE.paddingLeft;

        var pt = new PointText({
            ...DrawDisplayData.ECLEGEND_LABEL_STYLE,
            point: ptLoc,
            content: eText
        });

        pt.position = ptLoc;
        retGroup.addChild(pt);
        return retGroup;
    }

    getECRasterByID(eID: any) {
        var domImg = ApiService.getImageFromProductID(eID, this.adapter);
        if (!domImg) {
            return null;
        }
        var imgPos = new Point(DrawDisplayData.ECLEGEND_ICON_SIZE.width / 2, DrawDisplayData.ECLEGEND_ICON_SIZE.height / 2);//sets TopLeft at 0,0.
        var img = new Raster(domImg, imgPos);
        DrawDisplayData.positionLoadedRaster(img, DrawDisplayData.ECLEGEND_ICON_SIZE);
        return img;
    }

    getBacksplashRasterByID(upgradeProductID: any) {
        var domImg = ApiService.getImageFromProductID(upgradeProductID, this.adapter);
        if (!domImg) {
            return null;
        }
        var imgPos = new Point(DrawDisplayData.BACKSPLASH_LEGEND_ICON_SIZE.width / 2, DrawDisplayData.BACKSPLASH_LEGEND_ICON_SIZE.height / 2);//sets TopLeft at 0,0.
        var img = new Raster(domImg, imgPos);
        DrawDisplayData.positionLoadedRaster(img, DrawDisplayData.BACKSPLASH_LEGEND_ICON_SIZE);
        return img;
    }

    static positionLoadedRaster(img: any, size: any, position?: any, degrees?: any) {
        if (size)
            DrawDisplayData.resizeRaster(img, size);
        if (degrees)
            img.rotate(degrees);
        if (position)
            img.position = position;
    }

    static resizeRaster(img: any, size: any) {
        var scaleRatioH = size.height / img.height;
        var scaleRatioW = size.width / img.width;
        img.scale(scaleRatioW, scaleRatioH);
    }

    static getECLegendArrXShift() {
        return (DrawDisplayData.ECLEGEND_ICON_SIZE.width + DrawDisplayData.ECLEGEND_SPACER.width);
    }

    static getECLegendArrYShift() {
        return (DrawDisplayData.ECLEGEND_ICON_SIZE.height + DrawDisplayData.ECLEGEND_SPACER.height + DrawDisplayData.ECLEGEND_LABEL_SHIFT + DrawDisplayData.ECLEGEND_LABEL_STYLE.fontSize);
    }

    static getECLegendArrLocation() {
        //if (!ecLegendArr || ecLegendArr.length == 0) { return; }
        var xLoc, yLoc;

        //xLoc = CANVASWIDTH - (getECLegendArrXShift() * ecLegendArr.length);
        xLoc = (DrawDisplayData.ECLEGEND_POSITION.left) ? DrawDisplayData.ECLEGEND_SPACER.width : DrawDisplayData.CANVASWIDTH - (DrawDisplayData.getECLegendArrXShift());
        yLoc = (DrawDisplayData.ECLEGEND_POSITION.top) ? 2 * DrawDisplayData.getECLegendArrYShift() : DrawDisplayData.CANVASHEIGHT - (DrawDisplayData.getECLegendArrYShift() * 2);
        return new Point(xLoc, yLoc);
    }

    removeECLegendArrGroup() {
        if (this.currentECLegendArrGroup) {// && currentECLegendArr.length > 0) {
            this.currentECLegendArrGroup.remove();
        }
    }

    static createDisplayedAngle(canvas: any, adapter: EEToolAdapterContextType, dataObj: any, clickable: any, clickFunc: () => number, allowClickFunc: any): paper.PointText {
        var style = {...DrawDisplayData.ANGLE_DISPLAY_STYLE, ...{point: {}, content: ''}};
        style.point = dataObj.point;
        if (isNaN(dataObj.content)) {
            style.content = '';
        } else {
            style.content = '' + dataObj.content + '\xB0';
        }

        var angleText = new PointText(style);
        if (clickable) {
            angleText.on('mousedown', function () {
                if (!allowClickFunc || allowClickFunc()) {
                    adapter.showAngle(angleText.bounds.left + canvas.offsetLeft - window.scrollX,
                        angleText.bounds.top + canvas.offsetTop - window.scrollY + 30, clickFunc())
                }
            });
        }
        angleText.data = {isAngle: true};
        return angleText;
    }

    static createPointText(canvas: HTMLCanvasElement, adapter: EEToolAdapterContextType, ptData: any, doDisplayBoolFunc: any, onDisplayFunc: any, closingLogicFunc: any, onEnterPressFunc: any, style?: any) {
        if (!style) {
            style = DrawDisplayData.DEFAULT_MESASUREMENT_TEXT_STYLE;
        }
        style = {...{point: ptData.point, content: ptData.content + '"'}, ...style};
        let pt = new PointText(style);
        pt.onMouseDown = (evt: paper.MouseEvent) => {
            if (!doDisplayBoolFunc || doDisplayBoolFunc()) {
                adapter.showPointText(pt.bounds.left + canvas.offsetLeft - window.scrollX - 2,
                    pt.bounds.top + canvas.offsetTop - window.scrollY - 2,
                    DrawDisplayData.extractMeasure(pt.content),
                    (val: string) => {
                        if (onEnterPressFunc) {
                            onEnterPressFunc(val);
                        }
                    }
                );
                if (onDisplayFunc) {
                    onDisplayFunc(pt);
                }

                if (closingLogicFunc) {
                    closingLogicFunc(pt);
                }
            }
        };

        return pt;
    }

    static createEdgeColorPointText(drawDataObj: any): paper.PointText {
        var style = {...DrawDisplayData.MATERIAL_LABEL_STYLE, ...{point: {}, content: ''}};
        style.point = drawDataObj.point ? drawDataObj.point : drawDataObj.pointTextObj.point;
        style.content = drawDataObj.ec.pText + "\n" + drawDataObj.ec.cText;

        var pt = new PointText(style);

        drawDataObj.pointTextObj = pt;
        drawDataObj.point = null;
        return pt;
    }

    static extractMeasure(measure: any) {
        //ex: XXX in.
        //112.5\xB0
        if (!measure || measure.trim().length == 0) {
            return 0;
        }
        var unitIndex = measure.indexOf('in');
        if (unitIndex == -1) {
            unitIndex = measure.indexOf('\xB0');
        }
        measure = measure.slice(0, unitIndex).trim();
        return Number(measure);
    }

    static makeRasterBase(position: any, size: any, angle: any, raster: any) {
        var width, height;

        if (!size) {
            width = raster.size.width;
            height = raster.size.height;
            if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
                size = new SimpleSize(DrawDisplayData.NULL_SINK_SIZE.width, DrawDisplayData.NULL_SINK_SIZE.height);
            } else {
                size = new SimpleSize(width, height);
            }
        }

        var pointGroup = new Path.Rectangle(new Point(0, 0), size);
        pointGroup.strokeColor = new paper.Color('white');
        pointGroup.visible = false;
        pointGroup.position = position;
        if (angle)
            pointGroup.rotate(angle);

        raster.name = "SinkImage";
        pointGroup.name = "RasterTracker";
        return pointGroup;
    }
}


